import React from 'react';
import { useLocation } from 'react-router-dom';

export const PORTAL_TITLE = 'ITA | Portal';
const TITLE_SEPARATOR = ' \u2014 ';
const MAX_TITLE_LENGTH = 64;

const MAINTENANCE_PAGE_LABELS: Record<string, string> = {
  maintenance: 'Maintenance',
  users: 'Users',
  products: 'Products',
  items: 'Items',
  'items-nonserial': 'Non-serial Items',
  kits: 'Kits',
  charges: 'Charges',
  pricing: 'Pricing',
  'accounting-gl': 'Accounting GL',
  labor: 'Labor',
  service: 'Service',
  assets: 'Assets',
  stock: 'Stock',
  locations: 'Locations',
  sections: 'Sections',
  'items-panel-columns': 'Items Panel Columns',
  categories: 'Categories',
  'tax-codes': 'Tax Codes',
  'terms-and-conditions': 'Terms and Conditions',
  'payment-terms': 'Payment Terms',
  freelancers: 'Freelancers',
  vendors: 'Vendors',
  forms: 'Forms',
};

function cleanTitleContext(value: string | null | undefined): string {
  const printableValue = Array.from(String(value ?? ''), character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127 ? ' ' : character;
  }).join('');
  return printableValue
    .replace(/\s+/g, ' ')
    .trim();
}

export function formatPageTitle(context?: string | null): string {
  const cleanContext = cleanTitleContext(context);
  if (!cleanContext || cleanContext === PORTAL_TITLE) return PORTAL_TITLE;

  const availableLength = MAX_TITLE_LENGTH - PORTAL_TITLE.length - TITLE_SEPARATOR.length;
  const visibleContext = cleanContext.length <= availableLength
    ? cleanContext
    : `${cleanContext.slice(0, Math.max(1, availableLength - 1)).trimEnd()}…`;
  return `${PORTAL_TITLE}${TITLE_SEPARATOR}${visibleContext}`;
}

export function resolveRouteTitleContext(pathname: string, search = ''): string | null {
  const path = `/${String(pathname || '').split('?')[0].split('/').filter(Boolean).join('/')}`;
  const segments = path.split('/').filter(Boolean);

  if (path === '/') return 'Dashboard';
  if (path === '/login') return 'Sign In';
  if (path === '/reset-password') return 'Reset Password';
  if (path === '/accept-invite') return 'Accept Invitation';
  if (path === '/auth/callback') return 'Signing In';

  if (path === '/client-portal') return 'Client Portal';
  if (path.startsWith('/client-portal/invoices/')) return 'Client Portal · Invoice';
  if (path.startsWith('/client-portal/service/')) return 'Client Portal · Work Order';
  if (path.startsWith('/client-portal/')) return 'Client Portal · Event';
  if (path.startsWith('/s/') || path.startsWith('/sign/documents/')) return 'Sign Document';
  if (path.startsWith('/t/')) return 'Open Document Template';
  if (path.startsWith('/pay/')) return 'Payment';
  if (path.startsWith('/w/')) return 'Accept Work Order';
  if (path.startsWith('/installations/sign/completion/')) return 'Acknowledge Installation Completion';
  if (path.startsWith('/installations/sign/')) return 'Sign Installation Quote';
  if (path.startsWith('/schedule/shared/')) return 'Shared Schedule';

  if (path === '/events') return 'Events';
  if (path.includes('/order-chat/popout')) return 'Event Chat';
  if (path.startsWith('/events/')) return 'Event';

  if (path === '/installations') return 'Installations';
  if (path === '/installations/new') return 'New Installation';
  if (path.startsWith('/installations/')) return 'Installation';

  if (path === '/schedule' || path === '/calendar') return 'Schedule';
  if (path === '/clients') return 'Customers';
  if (path.startsWith('/clients/')) return 'Customer';
  if (path === '/venues') return 'Venues';
  if (path.startsWith('/venues/')) return 'Venue';

  if (path === '/transfers') {
    const warehouseTab = new URLSearchParams(search).get('tab');
    if (warehouseTab === 'requests') return 'Warehouse · Requests';
    if (warehouseTab === 'purchase_orders') return 'Warehouse · Purchase Orders';
    if (warehouseTab === 'subrentals' || warehouseTab === 'xrental_orders') return 'Warehouse · Subrentals';
    return 'Warehouse';
  }
  if (path.startsWith('/transfers/purchase-orders/')) return 'Purchase Order';
  if (path.startsWith('/transfers/xrental-orders/')) return 'Subrental';
  if (path.startsWith('/transfers/')) return 'Transfer Order';

  if (path === '/service') return 'Service';
  if (path === '/service/new') return 'New Service Ticket';
  if (path === '/service/scan') return 'Service Scanner';
  if (path.startsWith('/service/')) return 'Service Ticket';

  if (path === '/signature-documents') return 'Signatures';
  if (/^\/signature-documents\/[^/]+\/edit$/.test(path)) return 'Signature Document · Edit';
  if (/^\/signature-documents\/[^/]+\/sign$/.test(path)) return 'Sign Document';

  if (path === '/invoicing') return 'Invoicing';
  if (path === '/inventory') {
    const inventoryTab = new URLSearchParams(search).get('tab');
    if (inventoryTab === 'items') return 'Inventory · Items';
    if (inventoryTab === 'kits') return 'Inventory · Kits';
    if (inventoryTab === 'assets') return 'Inventory · Assets';
    if (inventoryTab === 'stock') return 'Inventory · Stock';
    if (inventoryTab === 'alerts') return 'Inventory · Alerts';
    if (inventoryTab === 'setup') return 'Inventory · Setup';
    if (inventoryTab === 'counts' || inventoryTab === 'scheduled') return 'Inventory · Physical Inventory';
    if (inventoryTab === 'yearly' || inventoryTab === 'yearly-requests') return 'Inventory · Yearly Requests';
    return 'Inventory';
  }
  if (path.startsWith('/inventory/counts/')) return 'Inventory · Count';
  if (path.startsWith('/inventory/items/')) return 'Inventory · Item';
  if (path.startsWith('/inventory/kits/')) return 'Inventory · Kit';
  if (path.startsWith('/inventory/assets/')) return 'Inventory · Asset';
  if (path === '/yearly-inventory') return 'Yearly Requests';
  if (path.includes('/workspace')) return 'Yearly Requests · Workspace';
  if (path.endsWith('/review')) return 'Yearly Requests · Review';
  if (path.endsWith('/planned-purchases')) return 'Yearly Requests · Planned Purchases';
  if (path.endsWith('/planned-transfers')) return 'Yearly Requests · Planned Transfers';
  if (path.startsWith('/yearly-inventory/rollups/')) return 'Yearly Requests · Rollup';
  if (path.startsWith('/yearly-inventory/')) return 'Yearly Requests';
  if (path.startsWith('/inventory-runs/')) return 'Inventory Run';

  if (path === '/profile') return 'Profile';
  if (path === '/profile/edit') return 'Profile · Edit';
  if (path === '/freelancer/profile') return 'Freelancer Profile';
  if (path === '/todo') return 'Planner';
  if (path === '/change-log') return 'Change Log';

  if (path === '/admin/settings') return 'Settings';
  if (path === '/admin/todo') return 'Planner · Admin';
  if (path === '/admin/suggestions') return 'Suggestions';

  const editorMatch = path.match(/^\/admin\/(products|items|assets|stock|locations|categories)\/editor$/);
  if (editorMatch) {
    const entityLabels: Record<string, string> = {
      products: 'Product',
      items: 'Item',
      assets: 'Asset',
      stock: 'Stock',
      locations: 'Location',
      categories: 'Category',
    };
    const editorIdParams: Record<string, string> = {
      products: 'productId',
      items: 'itemId',
      assets: 'assetId',
      stock: 'itemId',
      locations: 'locationId',
      categories: 'categoryId',
    };
    const hasRecord = new URLSearchParams(search).has(editorIdParams[editorMatch[1]]);
    return hasRecord ? `${entityLabels[editorMatch[1]]} · Edit` : `New ${entityLabels[editorMatch[1]]}`;
  }
  if (path === '/admin/forms/new') return 'New Form';
  if (/^\/admin\/forms\/[^/]+\/edit$/.test(path)) return 'Form · Edit';

  if (segments[0] === 'admin' && segments[1]) {
    const label = MAINTENANCE_PAGE_LABELS[segments[1]];
    if (label) return label === 'Maintenance' ? label : `Maintenance · ${label}`;
  }

  return null;
}

type PageTitleRegistration = {
  owner: symbol;
  pathname: string;
  context: string;
};

type PageTitleContextValue = {
  register: (registration: PageTitleRegistration) => void;
  unregister: (owner: symbol) => void;
};

const PageTitleContext = React.createContext<PageTitleContextValue | null>(null);
// The page's current title context ("0912130", "Events"), for the shell to read. Kept apart from the
// manager above so a title change never re-runs the pages' registrations.
const PageTitleReadContext = React.createContext<string | null>(null);

export function PageTitleProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [override, setOverride] = React.useState<PageTitleRegistration | null>(null);
  const register = React.useCallback((registration: PageTitleRegistration) => {
    setOverride(current => (
      current?.owner === registration.owner
      && current.pathname === registration.pathname
      && current.context === registration.context
        ? current
        : registration
    ));
  }, []);
  const unregister = React.useCallback((owner: symbol) => {
    setOverride(current => current?.owner === owner ? null : current);
  }, []);
  const value = React.useMemo(() => ({ register, unregister }), [register, unregister]);
  const routeContext = resolveRouteTitleContext(location.pathname, location.search);
  const activeContext = override?.pathname === location.pathname ? override.context : routeContext;
  const title = formatPageTitle(activeContext);

  React.useLayoutEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <PageTitleContext.Provider value={value}>
      <PageTitleReadContext.Provider value={activeContext}>{children}</PageTitleReadContext.Provider>
    </PageTitleContext.Provider>
  );
}

/** The current page's title context: a page's override (a record code) or the route's own ("Events"). */
export function usePageTitleContext(): string | null {
  return React.useContext(PageTitleReadContext);
}

export function usePageTitleOverride(context: string | null | undefined): void {
  const manager = React.useContext(PageTitleContext);
  const location = useLocation();
  const ownerRef = React.useRef(Symbol('page-title'));
  const cleanContext = cleanTitleContext(context);

  React.useLayoutEffect(() => {
    if (!manager || !cleanContext) return undefined;
    const owner = ownerRef.current;
    manager.register({ owner, pathname: location.pathname, context: cleanContext });
    return () => manager.unregister(owner);
  }, [cleanContext, location.pathname, manager]);
}
